<template>
  <div
    ref="trackEl"
    class="control-slider"
    :class="{ 'control-slider--disabled': disabled }"
    :style="rootStyle"
    role="slider"
    aria-orientation="horizontal"
    :tabindex="disabled ? -1 : 0"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="shown"
    :aria-disabled="disabled ? 'true' : undefined"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @lostpointercapture="onPointerUp"
    @keydown="onKeydown">
    <div class="control-slider-track">
      <div class="control-slider-fill" :style="{ width: percent + '%' }" />
    </div>
    <i class="control-slider-grip" :style="{ left: percent + '%' }" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { sheetAccentStyle } from './sheetAccent';

const props = withDefaults(
  defineProps<{
    /** The value; 0–100 unless `min`/`max` say otherwise. */
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
    /** Overrides the sheet's accent. */
    accent?: string;
    disabled?: boolean;
  }>(),
  { min: 0, max: 100, step: 1, disabled: false },
);

const emit = defineEmits<{
  'update:modelValue': [value: number];
  /** The released value — the moment a widget should write to the hub. */
  change: [value: number];
}>();

const trackEl = ref<HTMLElement | null>(null);
const dragging = ref(false);
/**
 * The last value this slider produced. The grip and the `change` payload read it
 * rather than `modelValue`: a parent's v-model updates land on the NEXT render,
 * so reading the prop inside the release handler would emit the value from
 * before the last pointermove — the stale step a widget would then write.
 */
const lastApplied = ref(props.modelValue);

const rootStyle = computed(() => sheetAccentStyle(props.accent));

const shown = computed(() => (dragging.value ? lastApplied.value : props.modelValue));

const percent = computed(() => {
  const span = props.max - props.min;
  if (span <= 0) return 0;
  return Math.max(0, Math.min(100, ((shown.value - props.min) / span) * 100));
});

/** Snap to the step grid, clamp to the range, and never hand back float noise
 *  (`(0.1 + 0.2) * 10` style) to a widget that writes it to the hub. */
function quantise(raw: number): number {
  const step = props.step > 0 ? props.step : 1;
  const stepped = Math.round((raw - props.min) / step) * step + props.min;
  const clamped = Math.max(props.min, Math.min(props.max, stepped));
  return Number(clamped.toFixed(6));
}

function valueFromPointer(event: PointerEvent): number {
  const el = trackEl.value;
  if (!el) return props.modelValue;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return props.modelValue;
  const ratio = (event.clientX - rect.left) / rect.width;
  return quantise(props.min + ratio * (props.max - props.min));
}

function apply(next: number, committed: boolean) {
  lastApplied.value = next;
  if (next !== props.modelValue) emit('update:modelValue', next);
  if (committed) emit('change', next);
}

function onPointerDown(event: PointerEvent) {
  if (props.disabled) return;
  // Capture: a drag that leaves the 10 px bar (or a button that shrinks under
  // the finger) keeps sending moves here instead of cancelling the gesture.
  try {
    trackEl.value?.setPointerCapture(event.pointerId);
  } catch {
    /* noop */
  }
  dragging.value = true;
  apply(valueFromPointer(event), false);
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value || props.disabled) return;
  apply(valueFromPointer(event), false);
}

function onPointerUp() {
  if (!dragging.value) return;
  dragging.value = false;
  emit('change', lastApplied.value);
}

function onKeydown(event: KeyboardEvent) {
  if (props.disabled) return;
  const big = props.step > 0 ? props.step * 10 : 10;
  let next: number | null = null;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowUp':
      next = quantise(shown.value + props.step);
      break;
    case 'ArrowLeft':
    case 'ArrowDown':
      next = quantise(shown.value - props.step);
      break;
    case 'PageUp':
      next = quantise(shown.value + big);
      break;
    case 'PageDown':
      next = quantise(shown.value - big);
      break;
    case 'Home':
      next = props.min;
      break;
    case 'End':
      next = props.max;
      break;
    default:
      return;
  }
  event.preventDefault();
  apply(next, true);
}
</script>

<style scoped>
/* The track owns the horizontal axis while a drag is on it; the sheet's own
   vertical scroll keeps the rest of the page. */
.control-slider {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --slider-accent: var(--sheet-accent, var(--device-color-light));
  position: relative;
  width: 100%;
  padding: 0.625rem 2px;
  cursor: pointer;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.control-slider--disabled {
  cursor: default;
  opacity: 0.45;
  pointer-events: none;
}

.control-slider-track {
  position: relative;
  height: 0.625rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.22);
  overflow: hidden;
}

.dark .control-slider-track {
  background: rgba(255, 255, 255, 0.08);
}

.control-slider-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: 999px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--slider-accent) 55%, transparent), var(--slider-accent));
}

.control-slider-grip {
  position: absolute;
  top: 50%;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  border: 2px solid var(--slider-accent);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

.control-slider:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--slider-accent) 60%, transparent);
  outline-offset: 2px;
  border-radius: 999px;
}
</style>
